import React, { useState, useContext } from 'react';

import AlertDialog, { AlertDialogProps as AlertItem } from './AlertDialog';
import ConfirmDialog, { ConfirmDialogProps as ConfirmItem } from './ConfirmDialog';
import PromptDialog, { PromptDialogProps as PromptItem } from './PromptDialog';

interface AlertOptions {
  title?: AlertItem['title']
  okText?: AlertItem['okText']
}

interface ConfirmOptions {
  title?: ConfirmItem['title']
  confirmText?: ConfirmItem['confirmText']
  cancelText?: ConfirmItem['cancelText']
}

interface PromptOptions {
  title?: PromptItem['title']
  submitText?: PromptItem['submitText']
  cancelText?: PromptItem['cancelText']
  fieldProps?: PromptItem['fieldProps']
}

export interface DialogContextState {
  alert: (message: AlertItem['message'], options?: AlertOptions) => Promise<void>
  confirm: (message: ConfirmItem['message'], options?: ConfirmOptions) => Promise<boolean>
  prompt: (message: PromptItem['message'], options?: PromptOptions) => Promise<string|null>
}

export const DialogContext = React.createContext<DialogContextState>({
  alert: async(message: AlertItem['message']) => window.alert(String(message)),
  confirm: async(message: ConfirmItem['message']) => window.confirm(message),
  prompt: async (message: PromptItem['message']) => window.prompt(message)
});

export const DialogProvider = (props: React.ComponentProps<any>) => {
  const [alertItems, setAlertItems] = useState<AlertItem[]>([]);
  const [confirmItems, setConfirmItems] = useState<ConfirmItem[]>([]);
  const [promptItems, setPromptItems] = useState<PromptItem[]>([]);

  const alert = (message: AlertItem['message'], options?: AlertOptions): Promise<void> => new Promise<void>(resolve => {
    const item: AlertItem = {
      title: options?.title || 'Alert',
      message: message,
      okText: options?.okText || 'Ok',
      callback() {
        resolve();
        setAlertItems(alertItems.filter(a => a !== item));
      }
    };
    setAlertItems([...alertItems, item]);
  });

  const confirm = (message: ConfirmItem['message'], options?: ConfirmOptions): Promise<boolean> => new Promise<boolean>(resolve => {
    const item: ConfirmItem = {
      title: options?.title || 'Confirm',
      message: message,
      confirmText: options?.confirmText || 'Confirm',
      cancelText: options?.cancelText || 'Cancel',
      callback(confirmed: boolean) {
        resolve(confirmed);
        setConfirmItems(confirmItems.filter(a => a !== item));
      }
    };
    setConfirmItems([...confirmItems, item]);
  });

  const prompt = (message: PromptItem['message'], options?: PromptOptions): Promise<string|null> => new Promise<string|null>(resolve => {
    const item: PromptItem = {
      title: options?.title || 'Prompt',
      message: message,
      submitText: options?.submitText || 'Submit',
      cancelText: options?.cancelText || 'Cancel',
      fieldProps: options?.fieldProps || {},
      callback(value: string|null) {
        resolve(value);
        setPromptItems(promptItems.filter(a => a !== item));
      }
    };
    setPromptItems([...promptItems, item]);
  });

  return (
    <DialogContext.Provider value={{alert, confirm, prompt}}>
      {alertItems.map((data, index) => (<AlertDialog {...data} key={index} />))}

      {confirmItems.map((data, index) => (<ConfirmDialog {...data} key={index} />))}

      {promptItems.map((data, index) => (<PromptDialog {...data} key={index} />))}

      {props.children}
    </DialogContext.Provider>
  );
};

export const useDialog = (): DialogContextState => useContext(DialogContext);
